import { safeErrorText } from 'app/modules/shared/utils/safe-log';
import { inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { environment } from 'environments/environment';
import { isValidMapsApiKey } from 'app/modules/shared/utils/third-party-data';
import {
    BehaviorSubject,
    catchError,
    delay,
    Observable,
    take,
    throwError,
} from 'rxjs';

export const gmResolver = () => {
    const gmService = inject(GoogleMapsService);
    return gmService.loadMapApi().pipe(
        take(1),
        delay(1000),
        catchError((error) => {
            // Log the error
            console.error(safeErrorText(error));

            // Throw an error
            return throwError(error);
        })
    );
};

@Injectable({
    providedIn: 'root',
})
export class GoogleMapsService {
    private renderer: Renderer2;
    private mapsLoaded = new BehaviorSubject<boolean>(false);
    private mapApiKey(): string {
        const key = (
            (typeof window !== 'undefined'
                ? (
                      window as Window & {
                          __ENV?: { MAP_API_KEY?: string };
                      }
                  ).__ENV?.MAP_API_KEY
                : undefined) ||
            environment.mapApiKey
        );
        // The key becomes part of a script URL: accept only what a Google Maps key consists of (BSI O.TrdP_7)
        return isValidMapsApiKey(key) ? key : '';
    }

    private mapsScriptUrl(): string {
        return `https://maps.googleapis.com/maps/api/js?key=${this.mapApiKey()}&loading=async&libraries=places&v=weekly`;
    }

    constructor(rendererFactory: RendererFactory2) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    // Method to load the script
    loadMapApi(): Observable<boolean> {
        if (this.mapsLoaded.value) {
            return this.mapsLoaded.asObservable();
        }

        this.loadScript();
        return this.mapsLoaded.asObservable();
    }

    private loadScript() {
        const gmScript = document.getElementById('gms');
        if (gmScript) {
            return;
        }
        const script = this.renderer.createElement('script');
        script.id = 'gms';
        script.type = 'text/javascript';
        script.src = this.mapsScriptUrl();
        script.async = true;
        script.defer = true;

        setTimeout(() => {
            script.onload = () => {
                this.mapsLoaded.next(true);
            };
        }, 2000);
        script.onerror = (error: any) => {
            this.mapsLoaded.error(error);
        };

        this.renderer.appendChild(document.body, script);
        console.log('load');
    }
}
